import React from 'react';

import typ from './index.d';
import {
  ChangePass,
  Image,
  ImageArea,
  ImageInput,
  Modal,
  PhotoIcon,
  TitleSection,
} from './styles';
import InputComponent from '../../input';
import EmptyImage from 'assets/images/empty-image.webp';
import { AdvisorCRUD } from '../../../services/requests/advisor';
import { toast } from 'react-toastify';
import { LoginCRUD } from '../../../services/requests/login';
import { useAppSelector } from '../../../hooks/useRedux';

const advisorCRUD = new AdvisorCRUD();
const loginCrud = new LoginCRUD();

const ConsultantModal: React.FC<typ.Props> = ({ onClose, consultantData }) => {
  // Redux
  const currentUser = useAppSelector(({state}) => state.user)

  // Refs
  const inputImageRef = React.useRef<HTMLInputElement>(null);

  // States
  const [img, setImg] = React.useState(consultantData?.image || '');
  const [imageBase64, setImageBase64] = React.useState<any>();

  const [name, setName] = React.useState(consultantData?.name || '');
  const [mail, setMail] = React.useState(consultantData?.email || '');
  
  const [isChangePass, setChangePass] = React.useState(false);
  const [oldPass, setOldPass] = React.useState('');
  const [pass, setPass] = React.useState('');
  const [confirmPass, setConfirmPass] = React.useState('');
  
  const [loading, setLoading] = React.useState(false)
  const [warning, setWarning] = React.useState({
    name: false,
    mail: false,
    pass: false,
    confirmPass: false,
    oldPass: false,
  });

  // Callbacks
  const createConsultant = React.useCallback(
    async (name: string, mail: string, pass: string, confirmPass: string, image: any) => {
      try {
        if (!name && !mail && !pass && !confirmPass) {
          setWarning(e => ({...e, mail: true, name: true, pass: true, confirmPass: true }));
          toast('Digite os campos corretamente.');
          return;
        }
        if (!name) {
          setWarning(e => ({ ...e, name: true }));
          toast('Digite o nome.');
          return;
        }
        if (!mail) {
          setWarning(e => ({ ...e, mail: true }));
          toast('Digite o email.');
          return;
        }
        if (!mail.includes('@')) {
          toast('Digite o email corretamente');
          setWarning(e => ({ ...e, mail: true }));
          return;
        }
        if (!pass) {
          setWarning(e => ({ ...e, pass: true }));
          toast('Digite a senha.');
          return;
        }
        if (!confirmPass) {
          setWarning(e => ({ ...e, confirmPass: true }));
          toast('Digite a senha novamente.');
          return;
        }
        
        if(pass !== confirmPass) {
          setWarning(e => ({ ...e, pass: true ,confirmPass: true }));
          toast('As senhas não são iguais.');
          return;
        }

        setLoading(true);

        const { status, error } = await advisorCRUD.create({
          name,
          mail,
          pass,
          photo: image,
        });

        if (error) throw new Error('Não foi possivel criar o consultor');

        if (status === 200) {
          toast('Consultor criado com sucesso.');
          onClose();
          setTimeout(() => {
            setLoading(false)
          }, 2000)
        }
      } catch (e) {
        const { message } = e as Error
        setLoading(false)
        toast.error(message);
      } 
    },
    [],
  );

  const editConsultant = React.useCallback(
    async (name: string, mail: string, pass: string, confirmPass: string, oldPass: string, image: string) => {
      try {
        if(isChangePass) {

          if (!oldPass) {
            setWarning(e => ({ ...e, oldPass: true }));
            toast('Digite a senha antiga.');
            return;
          }

          if (!pass) {
            setWarning(e => ({ ...e, pass: true }));
            toast('Digite a senha.');
            return;
          }

          if (!confirmPass) {
            setWarning(e => ({ ...e, confirmPass: true }));
            toast('Digite a senha novamente.');
            return;
          }

          if(pass !== confirmPass) {
            setWarning(e => ({ ...e, pass: true ,confirmPass: true }));
            toast('As senhas novas não são iguais.');
            return;
          }

          setLoading(true)

          const {  status , error } = await loginCrud.login({
            email: mail,
            password: oldPass,
          });

          if(status !== 200 || error) {
            toast.error('A senha antiga não confere.')
            setOldPass('')
            setLoading(false)
            return;
          }

        } else {
          if (!name) {
            setWarning(e => ({ ...e, name: true }));
            toast('Digite o nome.');
            return;
          }
          
          if (!mail) {
            setWarning(e => ({ ...e, mail: true }));
            toast('Digite o email.');
            return;
          }

          if (!mail.includes('@')) {
            toast('Digite o email corretamente');
            setWarning(e => ({ ...e, mail: true }));
            return;
          }
          setLoading(true)
        }
        
        const dataSend: typ.dataSend = {
          id: consultantData?.id || 0,
          photo: image,
          name, 
          mail,
        }

        if(pass) dataSend.pass = pass
        const { status, error } = await advisorCRUD.update(dataSend);

        if (error && isChangePass) throw new Error("Ocorreu um erro ao mudar a senha. Tente novamente");
        if (error) throw new Error("Ocorreu um erro ao editar o consultor.");
        
        if (status === 200) {
          setLoading(false)
          toast('Consultor editado com sucesso.');
          onClose();
        }
      } catch (e) {
        const { message } = e as Error
        setLoading(false)
        toast.error(message);
      }
    },
    [isChangePass],
  );

  const setToChangePassTrue = React.useCallback(() => {
    if(!isChangePass) {
      if(currentUser?.role === 'user') {
        toast('Você não tem permissão para mudar sua senha. Peça á alguém que tenha acesso ADMIN')
        return;
      }
      if (!mail) {
        setWarning(e => ({ ...e, mail: true }));
        toast('É preciso que o email esteja preenchido para alterar a senha.');
        return;
      }
    }

    setChangePass(!isChangePass)
  }, [isChangePass, mail])

  return (
    <Modal
      onClose={onClose}
      isLoading={loading}
      buttonTitle={`${consultantData ? 'Editar' : 'Enviar'}`}
      modalTitle={`${consultantData ? 'Editar' : 'Adicionar'} Consultador`}
      buttonCallback={() => {
        if (consultantData) {
          editConsultant(name, mail, pass,confirmPass, oldPass, imageBase64);
          return;
        }
        createConsultant(name, mail, pass, confirmPass, imageBase64);
      }}>
      {!isChangePass && (
        <>
          <ImageInput
            type="file"
            accept="image/*"
            ref={inputImageRef}
            onChange={event => {
              if (event && event.target?.files?.[0]) {
                const file = event.target.files[0];
    
                const reader = new FileReader();
                reader.onloadend = () => {
                  setImageBase64(reader.result);
                };
                reader.readAsDataURL(file);
                setImg(URL.createObjectURL(event.target.files[0]));
              }
            }}
          />
    
          <ImageArea onClick={() => inputImageRef.current?.click()}>
            <Image src={img ? img : EmptyImage} />
            <PhotoIcon />
          </ImageArea>
    
          <TitleSection>Dados Pessoais</TitleSection>
          <InputComponent
            placeholder="Nome"
            type="text"
            value={name}
            setValue={setName}
            customStyle={{ marginBottom: 14 }}
            warning={warning.name}
          />
          <InputComponent
            placeholder="Email"
            type="email"
            value={mail}
            setValue={setMail}
            customStyle={{ marginBottom: 14 }}
            warning={warning.mail}
          />
        </>
      )}

      {consultantData && <ChangePass style={{marginBottom: isChangePass ? 18: 0}} onClick={setToChangePassTrue}>{isChangePass ? "Voltar ao editar" : "Mudar a senha"}</ChangePass>}

      {(!consultantData || isChangePass) && (
        <>
          {consultantData && (
            <InputComponent
              placeholder="Senha atual"
              type="password"
              value={oldPass}
              setValue={setOldPass}
              customStyle={{ marginBottom: 14 }}
              warning={warning.oldPass}
            />
          )}
          <InputComponent
            placeholder="Senha"
            type="password"
            value={pass}
            setValue={setPass}
            customStyle={{ marginBottom: 14 }}
            warning={warning.pass}
          />
          <InputComponent
            placeholder="Confirmar Senha"
            type="password"
            value={confirmPass}
            setValue={setConfirmPass}
            customStyle={{ marginBottom: 0 }}
            warning={warning.confirmPass}
          />
        </>
      )}
    </Modal>
  );
};

export default ConsultantModal;
